<template>
  <Tab @close="removeCurrent">
    <template #toolbar-items>
      <!-- ------------------------ user ------------------------ -->
      <TabToolbarButton
        v-model="page"
        :menu="menu"
        :selected="page === 'user'"
        icon="fa-user"
        name="user"
      ></TabToolbarButton>
      <!-- ------------------------ entity ------------------------ -->
      <TabToolbarButton
        v-model="page"
        :menu="menu"
        :selected="page === 'entity'"
        icon="fa-building"
        name="entity"
      ></TabToolbarButton>
      <!-- ------------------------ plan ------------------------ -->
      <TabToolbarButton
        v-model="page"
        :menu="menu"
        :selected="page === 'plan'"
        icon="fa-cog"
        name="plan"
      ></TabToolbarButton>
      <!-- ------------------------ invoices ------------------------ -->
      <TabToolbarButton
        v-model="page"
        :menu="menu"
        :selected="page === 'invoices'"
        icon="fa-file-invoice-dollar"
        name="invoices"
      ></TabToolbarButton>
    </template>
    <template #toolbar-help>
      <v-btn class="tw-mr-2" fab text x-small :title="$t('dashboard.help.show')" @click="showHelp(menu,page)">
            <span class="mr-1">
              <v-icon color="primary" x-small>fa fa-question</v-icon>
            </span>
      </v-btn>
    </template>
    <template #container>
      <!-- ------------------------ entity ------------------------ -->
      <TabPage :menu="menu" :page="page" name="entity">
        <template #content>
          <v-container>
            <Form ref="company" :callback="updateCompany">
              <v-row id="account_company_id">
                <v-col cols="6">
                  <div class="sr-title">
                    {{ $t("dashboard.company.identification") }}
                  </div>
                </v-col>
                <v-col cols="6">
                  <div>
                    <span :title="$t('dashboard.company.identification')" class="sr-readonly-id">
                      {{ company.id }}
                    </span>
                  </div>
                </v-col>
              </v-row>
              <v-row id="account_company_logo">
                <v-col>
                  <AvatarField
                      v-if="company.hasOwnProperty('logo')"
                      :src="company.logo ? company.logo : 'https://cdn.securating.io/public/storage/shared/common/anonymous_company.png'"
                      i18n="account.form.logo"
                      with-upload
                      @uploaded="updateCompanyLogo"
                      content="logo"
                  />
                </v-col>
              </v-row>
              <v-row id="account_company_headline">
                <v-col cols="12">
                  <FormField
                    v-model="company.headline"
                    :props="{ maxLength: 255 }"
                    i18n="company.form.headline"
                    input-type="text"
                    rules="required"
                  />
                </v-col>
              </v-row>
              <v-row id="account_company_name">
                <v-col cols="6">
                  <FormField
                      v-model="company.company_name"
                      :props="{ maxLength: 100 }"
                      i18n="company.form.company_name"
                      input-type="text"
                      rules="required"
                  />
                </v-col>
                <v-col cols="6">
                  <FormField
                      v-model="company.department"
                      :props="{ maxLength: 50 }"
                      i18n="registration.form.company.department"
                      input-type="text"
                      not-required
                  />
                </v-col>
              </v-row>
              <v-row id="account_company_siret">
                <v-col cols="6">
                  <FormField
                    v-model="company.siret"
                    :props="{ maxLength: 14 }"
                    i18n="registration.form.company.siret"
                    input-type="text"
                    not-required
                  >
                    <template #append>
                      <Tooltip i18n="registration.help.company.siret" />
                    </template>
                  </FormField>
                </v-col>
                <v-col cols="6">
                  <FormField
                    v-model="company.vat_number"
                    :props="{ maxLength: 50 }"
                    i18n="registration.form.company.vat_number"
                    input-type="text"
                    not-required
                  />
                </v-col>
              </v-row>
              <v-row id="account_company_employe">
                <v-col cols="4">
                  <FormField
                    v-model="company.employees"
                    :props="{ min: 0, max: 3000000 }"
                    i18n="registration.form.company.employees"
                    input-type="number"
                  />
                </v-col>
                <v-col cols="8">
                  <!-- Special field to select the company -->
                  <CategoriesField :category.sync="company.category_id">
                    <template #append-outer>
                      <Tooltip i18n="registration.help.company.category" />
                    </template>
                  </CategoriesField>
                </v-col>
              </v-row>
              <div class="sr-title" id="account_company_adresse">
                {{ $t("dashboard.company.address") }}
              </div>
              <v-row id="account_company_street">
                <v-col cols="12">
                  <FormField
                    v-model="company.street"
                    :props="{ maxLength: 100 }"
                    i18n="registration.form.company.address.street"
                    input-type="text"
                  />
                </v-col>
              </v-row>
              <v-row id="account_company_further_information">
                <v-col cols="12">
                  <FormField
                    v-model="company.further_information"
                    :props="{ maxLength: 255 }"
                    i18n="registration.form.company.address.further_information"
                    input-type="text"
                  />
                </v-col>
              </v-row>
              <v-row id="account_company_country">
                <v-col cols="4">
                  <!-- Special field to select the country -->
                  <FormField
                    v-model="company.country"
                    :props="{ maxLength: 60 }"
                    i18n="registration.form.company.address.country"
                    input-type="text"
                  />
                </v-col>
                <v-col cols="4">
                  <FormField
                    v-model="company.zip"
                    :props="{ maxLength: 9 }"
                    i18n="registration.form.company.address.zip"
                    input-type="text"
                  />
                </v-col>
                <v-col cols="3">
                  <FormField
                    v-model="company.city"
                    :props="{ maxLength: 60 }"
                    i18n="registration.form.company.address.city"
                    input-type="text"
                  />
                </v-col>
              </v-row>
              <div class="sr-title" id="account_company_manager">
                {{ $t("dashboard.company.manager") }}
              </div>
              <v-row id="account_company_first_name">
                <v-col cols="6">
                  <FormField
                    v-model="company.first_name"
                    :props="{ maxLength: 50 }"
                    i18n="registration.form.company.first_name"
                    input-type="text"
                  />
                </v-col>
                <v-col cols="6">
                  <FormField
                    v-model="company.last_name"
                    :props="{ maxLength: 50 }"
                    i18n="registration.form.company.last_name"
                    input-type="text"
                  />
                </v-col>
              </v-row>
              <v-row id="account_company_email">
                <v-col cols="8">
                  <FormField
                    v-model="company.email"
                    :props="{ maxLength: 255 }"
                    i18n="company.form.email"
                    input-type="email"
                    rules="required|email"
                  />
                </v-col>
                <v-col cols="4">
                  <FormField
                    v-model="company.phone"
                    :props="{ maxLength: 15 }"
                    i18n="registration.form.company.phone"
                    input-type="tel"
                  />
                </v-col>
              </v-row>
              <v-row id="account_company_actions">
                <v-col cols="12">
                  <v-btn class="mr-6" color="primary" small @click="$refs.company.validate()">
                    {{ $t("common.update") }}
                  </v-btn>
                  <v-btn class="mr-6" small @click="cancel">
                    {{ $t("common.cancel") }}
                  </v-btn>
                </v-col>
              </v-row>
            </Form>
          </v-container>
        </template>
      </TabPage>
      <!-- ------------------------ plan ------------------------ -->
      <TabPage :menu="menu" :page="page" name="plan">
        <template #content>
          <v-container>
            <!-- planStatus 0: "Abonnement à régler"
                            1: "En attente du règlement"
                            2: "Règlement échoué,
                            3: "Règlement expiré"
                            4: "Paiement abandonné"
                            5: "Abonnement remboursé"
                            6: "Abonnement en attente de règlement"
                            7: "Abonnement en cours"
            -->
            <!-- ------------------------ SUBSCRIPTION LIST------------------------ -->

            <div class="float-right" style="position:relative; top:-5px;">
              <v-btn :title="$t('dashboard.company.plan.refresh')" icon @click="fetchSubscriptions">
                <v-icon x-small>
                  fa-sync-alt
                </v-icon>
              </v-btn>
              <v-btn
                v-if="!viewAllSubsciption"
                :title="$t('dashboard.company.plan.viewAllSubsciption.setToTrue')"
                icon
                @click="viewAllSubsciption = true"
              >
                <v-icon small>fas fa-file-medical</v-icon>
              </v-btn>
              <v-btn
                v-else
                :title="$t('dashboard.company.plan.viewAllSubsciption.setToFalse')"
                icon
                @click="viewAllSubsciption = false"
              >
                <v-icon small>fas fa-file</v-icon>
              </v-btn>
            </div>
            <v-list v-if="subscriptionsSubset.length >= 1">
              <v-subheader>{{ $t("dashboard.company.plan.list.title") }}</v-subheader>
              <v-list-item-group v-model="subscription" color="primary">
                <v-list-item v-for="(sub, index) in subscriptionsSubset" :key="index" three-line>
                  <v-list-item-avatar>
                    <v-icon :class="$t('dashboard.company.plan.planColor_' + sub.plan)" dark small>
                      {{ $t("dashboard.company.plan.planIcon_" + sub.plan) }}
                    </v-icon>
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title>
                      <span class="font-weight-bold">
                        {{ $t("dashboard.company.plan.planLabel_" + sub.plan) }}
                      </span>
                      <span>
                        -
                        {{ $t("dashboard.company.plan.planStatus_" + sub.subscription_status) }}
                      </span>
                      <span v-if="isSubscriptionStatus(sub, [2, 3, 4, 5])">
                        -
                        <v-icon small>fas fa-file-medical</v-icon>
                      </span>
                    </v-list-item-title>
                    <v-list-item-subtitle v-if="isSubscriptionStatus(sub, 7) && isSubscriptionPlan(sub, [1, 2, 3, 4])">
                      <span v-if="sub.hasOwnProperty('counter') && sub.counter !== null">
                        {{ $t("dashboard.company.plan.counter") }} {{ sub.counter.value }} /
                        {{ sub.counter.default_value }}
                      </span>
                      <span v-else>
                        {{ $t("dashboard.company.plan.counter") }} {{ getSolicitationAmount(sub) }} /
                        {{ getSolicitationAmount(sub) }}
                      </span>
                    </v-list-item-subtitle>
                    <v-list-item-subtitle v-if="isSubscriptionStatus(sub, [3, 7])">
                      {{ billingTranslation }},&nbsp;
                      <span v-if="isSubscriptionStatus(sub, 7)">
                        {{ $t("dashboard.company.plan.next") }} {{ sub.next_at | moment("DD/MM/YYYY") }}
                      </span>
                      <span v-if="isSubscriptionStatus(sub, 3)">
                        {{ $t("dashboard.company.plan.until") }} {{ sub.next_at | moment("DD/MM/YYYY") }}
                      </span>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list-item-group>
            </v-list>
            <div v-else v-html="$t('company.plan.noSubscription')"></div>
            <v-btn class="mt-4" color="primary" small @click="orderNewSubscription">
              {{ $t("dashboard.company.plan.order") }}
            </v-btn>
            <!-- ------------------------ DIALOGUE HOOK MOLLIE ------------------------ -->
            <v-dialog v-model="orderedDialog" max-width="90%" width="50%">
              <v-card v-if="orderedSubscription">
                <v-card-title>
                  <span class="headline">{{ $t("dashboard.company.plan.modal.ordered.title") }}</span>
                  <v-spacer />
                </v-card-title>
                <v-card-text
                  v-if="orderedSubscription.subscription_status > 0 && orderedSubscription.subscription_status < 8"
                >
                  <p
                    v-html="
                      $t('dashboard.company.plan.modal.ordered.status.' + orderedSubscription.subscription_status)
                    "
                  ></p>
                </v-card-text>
                <v-card-text v-else>
                  <p v-html="$t('dashboard.company.plan.modal.ordered.status.unknown')"></p>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="primary" @click="orderedDialog = false">
                    {{ $t("dashboard.company.plan.modal.ordered.cancel") }}
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <!-- ------------------------ DIALOGUE SUBSCRIPTION ACTIONS ------------------------ -->
            <v-dialog v-model="dialogUpdatePlan" max-width="90%" width="50%" @click:outside="subscription = -1">
              <v-card>
                <v-card-title>
                  <span class="headline">{{ $t("dashboard.company.plan.modal.update.title") }}</span>
                  <v-spacer />
                  <v-btn fab icon small @click="dialogUpdatePlan = false">
                    <v-icon small>fa fa-times</v-icon>
                  </v-btn>
                </v-card-title>
                <v-card-text v-if="subscriptions[subscription]">
                  <!-- ------------------- SUBSCRIPTION DETAILS --------------- -->
                  <v-list-item-avatar>
                    <v-icon
                      :class="$t('dashboard.company.plan.planColor_' + subscriptionsSubset[subscription].plan)"
                      dark
                      small
                    >
                      {{ $t("dashboard.company.plan.planIcon_" + subscriptionsSubset[subscription].plan) }}
                    </v-icon>
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title>
                      <span class="font-weight-bold">
                        {{ $t("dashboard.company.plan.planLabel_" + subscriptionsSubset[subscription].plan) }}
                      </span>
                      <span>
                        -
                        {{
                          $t(
                            "dashboard.company.plan.planStatus_" + subscriptionsSubset[subscription].subscription_status
                          )
                        }}
                      </span>
                      <span v-if="isSubscriptionStatus(subscriptionsSubset[subscription], [2, 3, 4, 5])">
                        -
                        <v-icon small>fas fa-file-medical</v-icon>
                      </span>
                    </v-list-item-title>
                    <!-- MODE + DATE -->
                    <v-list-item-subtitle v-if="isSubscriptionStatus(subscriptionsSubset[subscription], [3, 7])">
                      {{ billingTranslation }},&nbsp;
                      <span v-if="isSubscriptionStatus(subscriptionsSubset[subscription], 7)">
                        {{ $t("dashboard.company.plan.next") }}
                        {{ subscriptionsSubset[subscription].next_at | moment("DD/MM/YYYY") }}
                      </span>
                      <span v-if="isSubscriptionStatus(subscriptionsSubset[subscription], 3)">
                        {{ $t("dashboard.company.plan.until") }}
                        {{ subscriptionsSubset[subscription].next_at | moment("DD/MM/YYYY") }}
                      </span>
                    </v-list-item-subtitle>
                    <!-- TRANSACTION -->
                    <v-list-item-subtitle v-else>
                      {{ $t("dashboard.company.plan.created_at") }} {{ subscriptionsSubset[subscription].created_at }}
                    </v-list-item-subtitle>
                    <!-- SITES -->
                    <v-list-item-subtitle v-if="isSubscriptionStatus(subscriptionsSubset[subscription], [3, 7])">
                      {{
                        $tc("dashboard.company.plan.available_tokens", subscriptionsSubset[subscription].token_used, {
                          used: subscriptionsSubset[subscription].token_used,
                          amount: subscriptionsSubset[subscription].token_amount,
                        })
                      }}
                      <span v-if="subscriptionsSubset[subscription].token_used > 0">
                        &nbsp;(<span v-for="website in subscriptionsSubset[subscription].websites" v-bind:key="website">
                          {{ website }} </span
                        >)
                      </span>
                    </v-list-item-subtitle>

                    <!-- MONTANT -->
                    <v-list-item-subtitle>
                      {{ $t("dashboard.company.plan.amount") }} -
                      {{ getDisplayableString(subscriptionsSubset[subscription].amount_vat_excluded) }}
                      {{ $t("dashboard.company.plan.woTva") }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <!--                  <div v-html="$options.filters.pretty(subscriptions[subscription])"></div>-->
                </v-card-text>
                <v-card-actions v-if="subscriptionsSubset[subscription]">
                  <!-- ------------------- SUBSCRIPTION ACTIONS --------------- -->
                  <v-btn
                    v-if="isSubscriptionStatus(subscriptionsSubset[subscription], 7)"
                    color="primary"
                    @click="cancelSubscriptionRenewal(subscriptionsSubset[subscription])"
                  >
                    {{ $t("dashboard.company.plan.modal.update.cancel") }}
                  </v-btn>
                </v-card-actions>
                <v-card-actions v-else>
                  {{ $t("dashboard.company.plan.modal.update.nothing") }}
                </v-card-actions>
              </v-card>
            </v-dialog>
            <!-- ------------------------ DIALOGUE NEW SUBSCRIPTION ------------------------ -->
            <v-dialog v-model="dialog" max-width="100%">
              <v-row style="margin: 0;">
                <v-col cols="12" md="8">
                  <v-card v-if="plan !== null && billing !== null">
                    <v-card-title>
                      <span class="headline">{{ $t("dashboard.company.plan.modal.create.title") }}</span>
                    </v-card-title>
                    <v-card-text>
                      <div class="sr-title mt-2">
                        {{ $t("dashboard.company.plan.available") }}
                      </div>
                      <!-- ------------------------ TOP BUTTON PLAN CHOICE  ------------------------ -->
                      <div class="sr-subscription-plan-buttons mt-4">
                        <v-btn color="sr-subscription-plan-button-free" @click="changePlan(0)">
                          {{ $t("dashboard.company.plan.planLabel_0") }}
                        </v-btn>
                        <v-btn color="sr-subscription-plan-button-start" @click="changePlan(1)">
                          {{ $t("dashboard.company.plan.planLabel_1") }}
                        </v-btn>
                        <v-btn color="sr-subscription-plan-button-smart" @click="changePlan(2)">
                          {{ $t("dashboard.company.plan.planLabel_2") }}
                        </v-btn>
                        <v-btn color="sr-subscription-plan-button-max" @click="changePlan(3)">
                          {{ $t("dashboard.company.plan.planLabel_3") }}
                        </v-btn>
                      </div>
                      <!-- ------------------------ PLAN MONTH CHOICE ------------------------ -->
                      <hr class="mt-4" />
                      <div class="sr-title mt-4">
                        {{ $t("dashboard.company.plan.selected") }}
                      </div>
                      <div class="mt-2 sr-subscription-billing">
                        <div>
                          <span v-html="planTranslation"></span>
                          <span v-if="plan > 0" v-html="discountTranslation"></span>
                        </div>
                        <v-switch
                          v-if="plan > 0"
                          v-model="billing"
                          :false-value="0"
                          :label="billingTranslation"
                          :true-value="1"
                          flat
                          inset
                          @click="pricingCalculation"
                        ></v-switch>
                      </div>
                      <!-- ------------------------ PLAN INVOICE PRESENTATION ------------------------ -->
                      <hr class="mt-2" />
                      <div class="sr-title mt-6">
                        {{ $t("dashboard.company.plan.total") }}
                      </div>
                      <table class="sr-subscription-table mt-4">
                        <thead>
                          <tr>
                            <th>
                              {{ $t("dashboard.company.plan.preview.ref") }}
                            </th>
                            <th>
                              {{ $t("dashboard.company.plan.preview.qte") }}
                            </th>
                            <th>
                              {{ $t("dashboard.company.plan.preview.vat_excluded") }}
                            </th>
                            <th>
                              {{ $t("dashboard.company.plan.preview.total_vat_excluded") }}
                            </th>
                            <th>
                              {{ $t("dashboard.company.plan.preview.vat_rate") }}
                            </th>
                            <th>
                              {{ $t("dashboard.company.plan.preview.vat_amount") }}
                            </th>
                            <th>
                              {{ $t("dashboard.company.plan.preview.total_vat_included") }}
                            </th>
                          </tr>
                        </thead>
                        <tbody v-if="pricing !== {}">
                          <tr>
                            <!-- ------------------------ CELL PLAN NAME  ------------------------ -->
                            <td>
                              <v-btn v-if="plan === 0" color="sr-subscription-plan-button-free">
                                {{ $t("dashboard.company.plan.planLabel_0") }}
                              </v-btn>
                              <v-btn v-if="plan === 100" color="sr-subscription-plan-button-discover">
                                {{ $t("dashboard.company.plan.planLabel_100") }}
                              </v-btn>
                              <v-btn v-if="plan === 1" color="sr-subscription-plan-button-start">
                                {{ $t("dashboard.company.plan.planLabel_1") }}
                              </v-btn>
                              <v-btn v-if="plan === 2" color="sr-subscription-plan-button-smart">
                                {{ $t("dashboard.company.plan.planLabel_2") }}
                              </v-btn>
                              <v-btn v-if="plan === 3" color="sr-subscription-plan-button-max">
                                {{ $t("dashboard.company.plan.planLabel_3") }}
                              </v-btn>
                              <v-btn v-if="plan === 4" color="sr-subscription-plan-button-nolimit">
                                {{ $t("dashboard.company.plan.planLabel_4") }}
                              </v-btn>
                            </td>
                            <!-- ------------------------ CELL DURATION  ------------------------ -->
                            <td>
                              {{ pricing.billingDuration }}
                            </td>
                            <!-- ------------------------ CELL UNIT PRICE  ------------------------ -->
                            <td v-if="pricing.realAmountVatExcluded > pricing.amountVatExcluded">
                              <div class="sr-subscription-price-discount">
                                {{ pricing.amountVatExcluded }}
                              </div>
                              <div class="sr-subscription-price-real">
                                {{ pricing.realAmountVatExcluded }}
                              </div>
                            </td>
                            <td v-else>
                              {{ pricing.amountVatExcluded }}
                            </td>
                            <!-- ------------------------ CELL TOTAL HT  ------------------------ -->
                            <td>
                              {{ pricing.totalAmountVatExcluded }}
                            </td>
                            <!-- ------------------------ CELL TVA % ------------------------ -->
                            <td>
                              {{ pricing.defaultVatRate }}
                            </td>
                            <!-- ------------------------ CELL TVA TO PAY  ------------------------ -->
                            <td>
                              {{ pricing.totalVatAmount }}
                            </td>
                            <!-- ------------------------ CELL TOTAL TTC  ------------------------ -->
                            <td>
                              {{ pricing.totalAmountVatIncluded }}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </v-card-text>
                    <v-card-actions class="sr-payment-button mt-4">
                      <!-- ------------------------ PLAN BUTTON ORDER  ------------------------ -->
                      <v-btn
                        v-if="pricing.orderAmountVatIncluded > 0"
                        block
                        class="sr-subscription-pricing-button"
                        color="primary"
                        @click="payTheNewPlan"
                      >
                        <span>{{ $t("dashboard.company.plan.modal.create.pay") }}</span>
                        <span>|</span>
                        <span>{{ pricing.totalAmountVatIncluded }}</span>
                      </v-btn>
                      <v-btn v-else block class="sr-subscription-pricing-button" disabled>
                        <span>{{ $t("dashboard.company.plan.modal.create.isFree") }}</span>
                      </v-btn>
                    </v-card-actions>

                    <a class="sr-disclaimer-link" href="https://www.mollie.com/fr" rel="noopener" target="_blank">
                      {{ $t("dashboard.company.plan.secure_by_mollie") }}
                    </a>
                  </v-card>
                </v-col>
                <v-col class="d-none d-md-block" cols="4">
                  <iframe id="iframe-plan-view" :src="getUrlPlanDetail"></iframe>
                </v-col>
              </v-row>
            </v-dialog>
          </v-container>
        </template>
      </TabPage>
      <!-- ------------------------ invoices ------------------------ -->
      <TabPage :menu="menu" :page="page" name="invoices" >
        <template #content>
          <v-row>
            <v-col :cols="invoiceColWidth">
              <v-card class="sr-container">
                <v-container class="sr-over-container">
                  <v-btn style="position: absolute;right: 20px; top: 10px;" :title="$t('company.invoices.refresh')" icon @click="fetchInvoices">
                    <v-icon x-small>
                      fa-sync-alt
                    </v-icon>
                  </v-btn>
                  <span class="sr-title">
                    {{ $t("company.invoices.title") }}
                  </span>

                  <v-card-text>
                    <v-simple-table>
                      <thead>
                        <tr>
                          <th class="text-left">
                            {{ $t("company.invoices.name") }}
                          </th>
                          <th class="text-left">
                            {{ $t("company.invoices.number") }}
                          </th>
                          <th class="text-left">
                            {{ $t("company.invoices.created_at") }}
                          </th>
                          <th class="text-left">
                            {{ $t("company.invoices.price_net") }}
                          </th>
                          <th class="text-left">
                            {{ $t("company.invoices.price_gross") }}
                          </th>
                          <th class="text-left">
                            {{ $t("company.invoices.actions") }}
                          </th>
                        </tr>
                      </thead>
                      <tbody v-if="invoices.length > 0">
                        <tr
                          v-for="item in invoices"
                          :key="item.id"
                          :class="['tw-cursor-pointer', item.download ? 'green lighten-3' : '']"
                        >
                          <td @click="openPdf(item)">
                            {{ item.title }}
                          </td>
                          <td @click="openPdf(item)">
                            {{ item.number }}
                          </td>
                          <td @click="openPdf(item)">
                            {{ item.created_at | moment("DD/MM/YYYY") }}
                          </td>
                          <td @click="openPdf(item)">{{ item.price_net }} €</td>
                          <td @click="openPdf(item)">{{ item.price_gross }} €</td>
                          <td>
                            <v-btn icon small @click="openPdf(item)">
                              <v-icon small>fa-eye</v-icon>
                            </v-btn>
                            <v-btn icon small @click="downloadPdf(item)">
                              <v-icon small>fa-download</v-icon>
                            </v-btn>
                          </td>
                        </tr>
                      </tbody>
                      <tbody v-else>
                        <tr>
                          <td class="tw-text-center tw-w-full" colspan="6" style="height:100px">
                            {{ $t("common.no_results") }}
                          </td>
                        </tr>
                      </tbody>
                    </v-simple-table>
                  </v-card-text>
                </v-container>
              </v-card>
            </v-col>
            <v-col :cols="invoiceColWidth" v-if="pdf.length > 0">
              <v-card class="sr-over-container sr-max">
                <iframe :src="iframeSrc" height="100%" width="100%"></iframe>
              </v-card>
            </v-col>
          </v-row>
        </template>
      </TabPage>
      <!-- ------------------------ user ------------------------ -->
      <TabPage :menu="menu" :page="page" name="user">
        <template #content>
          <!-- Form to update user data -->
          <Form ref="account" :callback="updateUser">
            <AvatarField
              v-if="user.hasOwnProperty('avatar')"
              :src="
                user.avatar
                  ? user.avatar
                  : 'https://cdn.securating.io/public/storage/shared/common/anonymous_avatar1.png'
              "
              i18n="account.form.avatar"
              with-upload
              @uploaded="updateUserAvatar"
            />

            <FormField
              v-model="user.email"
              i18n="account.form.email"
              input-type="email"
              read-only
              rules="required|email"
            />

            <FormField
              v-model="user.user_name"
              i18n="account.form.user_name"
              input-type="text"
              read-only
              rules="required"
            />

            <FormField v-model="user.first_name" i18n="account.form.first_name" input-type="text" />

            <FormField v-model="user.last_name" i18n="account.form.last_name" input-type="text" />

            <div>
              <v-btn class="mr-6" color="primary" small @click="$refs.account.validate()">
                {{ $t("common.update") }}
              </v-btn>
              <v-btn class="mr-6" small @click="cancelUser">
                {{ $t("common.cancel") }}
              </v-btn>
            </div>
          </Form>
        </template>
        <template #right>
          <v-card>
            <v-list-item>
              <v-list-item-avatar class="tw-border tw-border-gray-600">
                <v-img
                  :src="
                    user.avatar
                      ? user.avatar
                      : 'https://cdn.securating.io/public/storage/shared/common/anonymous_avatar1.png'
                  "
                ></v-img>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="headline">
                  {{ publicName }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ $t("account.user.right.card.moderator") }}
                  @{{ companyName }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>

            <v-card-text>
              <!-- More infos here... -->
            </v-card-text>
          </v-card>
        </template>
      </TabPage>
    </template>
  </Tab>
</template>

<script>
import tab from "../../../../mixins/tab"
import { isset } from "@/helpers"
import CategoriesField from "@/components/common/Form/CategoriesField"

export default {
  name: "Account",
  mixins: [tab],
  components: {
    CategoriesField,
  },
  data: () => ({
    user: {},
    uploadingAvatar: false,
    valid: false,
    pages: ["entity", "plan", "invoices", "user", "secret"],
    menu: "account",
    page: "user",
    edit: false,
    viewAllSubsciption: false,
    findCitiesTimeout: 500,
    cityAutoComplete: false,
    cities: [],
    timeoutHandler: null,
    dialog: false,
    company: {},
    invoices: [],
    pdf: "",
    dialogUpdatePlan: false,
    plan: 1,
    billing: 0,
    ordered: null,
    constants: [],
    pricing: {},
    subscriptions: [],
    subscription: -1,
    mollieCheckoutUrl: null,
    orderedDialog: false,
    orderedSubscription: null,
  }),
  watch: {
    subscription(newVal) {
      if (this.subscriptions[newVal]) {
        this.dialogUpdatePlan = true
      }
    },
    dialogUpdatePlan(newVal) {
      if (newVal === false) {
        this.subscription = -1
      }
    },
  },
  async mounted() {
    this.reloadPreviousOpenedTab()
    await this.fetchUser()
    await this.fetchSubscriptions()

    this.bindQueryNumber("plan")
    this.bindQueryNumber("billing")
    this.bindQuery("ordered")

    //-- Urls used for tests purpose
    //-- ORDER STATUS 1 : http://localhost:8081/?ordered=772b2f643132edfe563e9400d2ae05add383eea0
    //-- ORDER STATUS 2 : http://localhost:8081/?ordered=772b2f643132edfe563e9400d2ae05add383eea0
    //-- ORDER STATUS 3 : http://localhost:8081/?ordered=772b2f643132edfe563e9400d2ae05add383eea0
    //-- ORDER STATUS 4 : http://localhost:8081/?ordered=772b2f643132edfe563e9400d2ae05add383eea0
    //-- ORDER STATUS 5 : http://localhost:8081/?ordered=772b2f643132edfe563e9400d2ae05add383eea0
    //-- ORDER STATUS 6 : http://localhost:8081/?ordered=772b2f643132edfe563e9400d2ae05add383eea0
    //-- ORDER STATUS 7 : http://localhost:8081/?ordered=772b2f643132edfe563e9400d2ae05add383eea0

    if (this.ordered !== null) {
      await this.loadOrderedSubscription(this.ordered)
      if (this.orderedSubscription) {
        this.page = "plan"
        this.orderedDialog = true
      }
      this.cleanUrl()
    }

    if (this.plan !== null && this.billing !== null) {
      // Ensure that the order page is opened in addition to the defaultPage parameter
      this.page = "plan"
      this.dialog = true
      this.cleanUrl()
      this.pricingCalculation()
    } else {
      // Else we need to set default value to pay something
      if (this.plan == null) {
        this.plan = 1
      }
      if (this.billing == null) {
        this.billing = 0
      }
    }
    // Try to fetch the company related to the user
    await this.fetchCompany()
    this.fetchInvoices()
  },
  computed: {
    invoiceColWidth() {
      if(this.pdf.length > 0) { return 6 } else { return 12 }
    },
    subscriptionsSubset() {
      if (this.viewAllSubsciption) {
        return this.subscriptions
      } else {
        return this.subscriptions.filter(function(sub) {
          let noWantedstatus = [2, 3, 4, 5]
          return !noWantedstatus.includes(sub.subscription_status)
        })
      }
    },
    getUrlPlanDetail() {
      let planDetailUrl = "https://securating.com/abonnement-"
      let planDetailUrlAbo = "START"
      if (this.plan === 0) planDetailUrlAbo = "FREE"
      if (this.plan === 100) planDetailUrlAbo = "DISCOVER"
      if (this.plan === 2) planDetailUrlAbo = "SMART"
      if (this.plan === 3) planDetailUrlAbo = "MAX"
      if (this.plan === 4) planDetailUrlAbo = "NOLIMIT"
      return planDetailUrl + planDetailUrlAbo
    },
    iframeSrc() {
      if (this.pdf.length > 0) {
        return "data:application/pdf;base64, " + this.pdf
      }
      return "about:blank"
    },
    planTranslation() {
      const planLabel = this.plan !== null && this.between(this.plan, 0, 4) ? this.plan : 0
      const plan = this.$t("dashboard.company.plan.planLabel_" + planLabel)
      return this.$t("dashboard.company.plan.planTranslation", {
        plan,
      })
    },
    discountTranslation() {
      const billingLabel = this.billing !== null && this.between(this.billing, 0, 1) ? this.billing : 0

      return billingLabel > 0
        ? this.$t("dashboard.company.plan.discountTranslation", { discount: this.pricing.billingReduction })
        : this.$t("dashboard.company.plan.noDiscountTranslation")
    },
    billingTranslation() {
      const billingLabel = this.billing !== null && this.between(this.billing, 0, 1) ? this.billing : 0
      const billing = this.$t("dashboard.company.plan.billingLabel_" + billingLabel)
      return this.$t("dashboard.company.plan.billingTranslation", {
        billing,
      })
    },
    publicName() {
      if (
        isset(this.user.first_name) &&
        this.user.first_name.length > 0 &&
        isset(this.user.last_name) &&
        this.user.last_name.length > 0
      ) {
        return `${this.user.first_name.charAt(0).toUpperCase() +
          this.user.first_name.slice(1)} ${this.user.last_name.slice(0, 1).toUpperCase()}.`
      }
      if (isset(this.user.first_name) && this.user.first_name.length > 0) {
        return this.user.first_name
      }

      return this.user.user_name
    },
    companyName() {
      return this.company.company_name
    },
  },
  methods: {
    async loadOrderedSubscription(hash) {
      await this.$axios.get("office/manager/subscription/" + hash).then(response => {
        this.orderedSubscription = response.data
      })
    },
    cleanUrl() {
      // Reset navigation
      delete this.$route.query.plan
      delete this.$route.query.billing
      delete this.$route.query.ordered
      history.replaceState(null, "", location.href.split("?")[0])
    },
    isSubscriptionStatus(subscription, status) {
      if (Array.isArray(status)) {
        return status.includes(subscription.subscription_status)
      } else {
        return subscription.subscription_status === status
      }
    },
    isSubscriptionPlan(subscription, plans) {
      if (Array.isArray(plans)) {
        return plans.includes(subscription.plan)
      } else {
        return subscription.plan === plans
      }
    },
    getSolicitationAmount(subscription) {
      return this.get("PLAN_SOLICITATION_AMOUNT")[subscription.plan]
    },
    between(value, min, max) {
      return value >= min && value <= max
    },
    bindQueryNumber(name) {
      this[name] = this.$route.query.hasOwnProperty(name) ? Number(this.$route.query[name]) : null
    },
    bindQuery(name) {
      this[name] = this.$route.query.hasOwnProperty(name) ? this.$route.query[name] : null
    },
    fetchInvoices() {
      this.$axios.get("office/manager/subscription/invoices").then(response => {
        this.invoices = response.data.data
      })
    },
    openPdf(item) {
      this.$axios
        .get(`office/manager/subscription/invoices/pdf?number=${item.number}&token=${item.token}`)
        .then(response => {
          this.pdf = response.data.data
        })
    },
    downloadPdf(item) {
      this.$axios
        .get(`office/manager/subscription/invoices/pdf?number=${item.number}&token=${item.token}`)
        .then(response => {
          this.pdf = response.data.data

          const linkSource = this.iframeSrc
          const downloadLink = document.createElement("a")
          const fileName = `Facture-Securating-${item.number.replace("/", "_")}.pdf`

          downloadLink.href = linkSource
          downloadLink.download = fileName
          downloadLink.click()

          item.download = true
        })
    },
    fetchCompany() {
      return new Promise((resolve, reject) => {
        this.$axios
          .get("office/manager/users/companies")
          .then(response => {
            this.company = { ...this.company, ...response.data }
            resolve(this.company)
          })
          .catch(error => reject(error))
      })
    },
    updateCompany() {
      return new Promise((resolve, reject) => {
        this.$axios
          .patch("office/manager/companies/details", this.company)
          .then(() => {
            resolve()
          })
          .catch(errors => {
            reject(errors.response.data)
          })
      })
    },
    // Cancel the current modification on the user data
    cancel() {
      this.formErrors = []
      this.edit = false
      this.fetchCompany()
    },
    payTheNewPlan() {
      this.$axios
        .post("office/manager/subscription", {
          plan: this.plan,
          billing: this.billing,
        })
        .then(response => {
          this.mollieCheckoutUrl = response.data.data.checkout
          window.location.href = this.mollieCheckoutUrl
        })
    },
    async fetchSubscriptions() {
      await this.$axios.get("office/manager/subscription").then(response => {
        this.constants = response.data.data.constants
        this.subscriptions = response.data.data.subscriptions
      })
    },
    get(constant, defaultValue = null) {
      if (this.constants.hasOwnProperty(constant)) {
        return this.constants[constant]
      }
      return defaultValue
    },
    getDisplayableString(price, symbol = "€") {
      return [Number(price).toFixed(2), symbol].join(" ")
    },
    pricingCalculation() {
      // To know the right VAT rate
      let billWithVat = !this.company["vat_number_approved"]

      // Choose the right VAT rate
      let defaultVatRate = billWithVat ? this.get("VAT_RATE_DEFAULT") : this.get("VAT_RATE_APPROVED")

      // Find billing reduction
      let billingReduction = this.get("BILLING_DISCOUNT")[this.billing]

      // Get the the real plan amount
      let realAmountVatExcluded = this.get("PLAN_PRICE")[this.plan]

      // Discount calculation
      let amountVatExcluded = realAmountVatExcluded - realAmountVatExcluded * (billingReduction / 100)

      // Vat calculation
      let amountVatIncluded = amountVatExcluded + amountVatExcluded * (defaultVatRate / 100)
      let vatAmount = amountVatIncluded - amountVatExcluded

      // Fet billing duration
      let billingDuration = this.get("BILLING_DURATION")[this.billing]

      let totalAmountVatExcluded = amountVatExcluded * billingDuration

      let totalAmountVatIncluded = totalAmountVatExcluded + totalAmountVatExcluded * (defaultVatRate / 100)

      let totalVatAmount = totalAmountVatIncluded - totalAmountVatExcluded

      this.pricing = {
        orderAmountVatIncluded: totalAmountVatIncluded,
        realAmountVatExcluded: this.getDisplayableString(realAmountVatExcluded),
        amountVatExcluded: this.getDisplayableString(amountVatExcluded),
        defaultVatRate: this.getDisplayableString(defaultVatRate, "%"),
        amountVatIncluded: this.getDisplayableString(amountVatIncluded),
        vatAmount: this.getDisplayableString(vatAmount),
        billingDuration,
        billWithVat,
        billingReduction: this.getDisplayableString(billingReduction, "%"),
        totalAmountVatExcluded: this.getDisplayableString(totalAmountVatExcluded),
        totalAmountVatIncluded: this.getDisplayableString(totalAmountVatIncluded),
        totalVatAmount: this.getDisplayableString(totalVatAmount),
      }
    },
    changePlan(plan = 1) {
      this.plan = plan
      this.pricingCalculation()
    },
    orderNewSubscription() {
      this.dialog = true
      this.changePlan(this.plan)
    },
    cancelSubscriptionRenewal(subscriptionToCancel) {
      if (subscriptionToCancel) {
        this.$axios
          .delete("office/manager/subscription", {
            data: {
              hash: subscriptionToCancel.hash,
            },
          })
          .then(response => {
            subscriptionToCancel = response.data.data
            this.dialogUpdatePlan = false
          })
      }
    },
    fetchUser() {
      return new Promise((resolve, reject) => {
        this.$axios
          .get("office/manager/user/")
          .then(response => {
            this.user = response.data
            resolve(this.company)
          })
          .catch(error => reject(error))
      })
    },
    // Cancel the current modification on the user data
    cancelUser() {
      this.fetchUser()
    },
    // Update the current user
    updateUser() {
      return new Promise((resolve, reject) => {
        this.$axios
          .put("/office/manager/users", { ...this.user })
          .then(() => {
            resolve()
          })
          .catch(errors => reject(errors.response.data))
      })
    },
    updateUserAvatar(image) {
      this.user.avatar = image.url
      this.updateUser()
    },
    updateCompanyLogo(image) {
      this.company.avatar = image.url
      this.updateCompany()
    },
    fetchCompanyUser() {
      this.$axios.get("office/manager/users/companies").then(response => {
        this.company = { ...this.company, ...response.data }
      })
    },
  },
}
</script>
